import React from 'react';
import { ui, msgColor } from '../../styles/shared';
import { Device } from '../../types';

// Stilar från den gemensamma stilfilen
const card = ui.card;
const btn = ui.btn;
const inputStyle = ui.input;
const labelStyle = ui.label;
const hintStyle = ui.hint;

interface Props {
    device: Device;
    sensorData: any;
    settingsForm: Record<string, string>;
    settingsSaving: boolean;
    settingsMsg: string | null;
    zigbeeConfigForm: Record<string, string>;
    zigbeeConfigSaving: boolean;
    zigbeeConfigMsg: string | null;
    onLoadSensorData: () => void;
    onSettingsChange: (fn: (prev: Record<string, string>) => Record<string, string>) => void;
    onSaveSettings: (payload: Record<string, any>) => void;
    onZigbeeConfigChange: (fn: (prev: Record<string, string>) => Record<string, string>) => void;
    onApplyZigbeeConfig: (attrs: Record<string, number>) => void;
}

const PowerMeterControl: React.FC<Props> = ({
    device, sensorData,
    settingsForm, settingsSaving, settingsMsg,
    zigbeeConfigForm, zigbeeConfigSaving, zigbeeConfigMsg,
    onLoadSensorData, onSettingsChange, onSaveSettings,
    onZigbeeConfigChange, onApplyZigbeeConfig,
}) => {
    const d = sensorData || {};
    const power   = d.power   ?? device?.power;
    const energy  = d.energy  ?? device?.energy;
    const voltage = d.voltage ?? device?.voltage;
    const current = d.current ?? device?.current;
    const hasData = power != null || energy != null || voltage != null || current != null;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Live readings */}
            <div style={card}>
                <h3 style={{ marginTop: 0 }}>⚡ Elmätare</h3>
                <div style={{ marginBottom: '20px' }}>
                    <button onClick={onLoadSensorData} style={{ ...btn('#007bff'), padding: '12px 24px' }}>Uppdatera avläsning</button>
                </div>
                <div style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '4px', border: '1px solid #ddd' }}>
                    <h4 style={{ marginTop: 0 }}>Aktuella värden:</h4>
                    {power != null && <p style={{ fontSize: '22px', fontWeight: 'bold', margin: '10px 0', color: '#e67e22' }}>⚡ Effekt: {power} W</p>}
                    {energy != null && <p style={{ fontSize: '22px', fontWeight: 'bold', margin: '10px 0', color: '#27ae60' }}>🔋 Total energi: {energy} kWh</p>}
                    {voltage != null && <p style={{ fontSize: '18px', fontWeight: 'bold', margin: '10px 0', color: '#2980b9' }}>🔆 Spänning: {voltage} V</p>}
                    {current != null && <p style={{ fontSize: '18px', fontWeight: 'bold', margin: '10px 0', color: '#8e44ad' }}>〰️ Ström: {current} A</p>}
                    {!hasData && <p style={{ color: '#888' }}>Inga värden ännu. De kommer in automatiskt när mätaren rapporterar.</p>}
                </div>
            </div>

            {/* Configuration */}
            <div style={card}>
                <h3 style={{ marginTop: 0 }}>⚙️ Inställningar</h3>
                <p style={{ fontSize: '14px', color: '#666', marginBottom: '16px' }}>
                    För mätare som räknar pulser (lampan som blinkar på elmätaren): fyll i pulstalet som står på mätaren, oftast 1000 imp/kWh.
                    Lämna 0 för att använda värdena precis som enheten skickar dem.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', maxWidth: '400px' }}>
                    <div>
                        <label style={labelStyle}>Pulser per kWh (imp/kWh)</label>
                        <input type="number" min="0" step="100" placeholder="t.ex. 1000"
                            value={settingsForm.pulses_per_kwh ?? ''}
                            onChange={e => onSettingsChange(p => ({ ...p, pulses_per_kwh: e.target.value }))}
                            style={inputStyle} />
                        <span style={hintStyle}>Vanliga värden: 1000, 2000, 500. Titta på etiketten på mätaren.</span>
                    </div>
                    <div>
                        <label style={labelStyle}>Justering av energi (kWh)</label>
                        <input type="number" step="0.001" placeholder="t.ex. 0"
                            value={settingsForm.energy_offset ?? ''}
                            onChange={e => onSettingsChange(p => ({ ...p, energy_offset: e.target.value }))}
                            style={inputStyle} />
                        <span style={hintStyle}>Frivilligt: värdet läggs till på alla energiavläsningar.</span>
                    </div>
                    <button disabled={settingsSaving}
                        onClick={() => {
                            const payload: Record<string, any> = {};
                            if (settingsForm.pulses_per_kwh) payload.pulses_per_kwh = Number(settingsForm.pulses_per_kwh);
                            if (settingsForm.energy_offset)  payload.energy_offset  = Number(settingsForm.energy_offset);
                            onSaveSettings(payload);
                        }}
                        style={{ ...btn(settingsSaving ? '#6c757d' : '#28a745'), cursor: settingsSaving ? 'not-allowed' : 'pointer', alignSelf: 'flex-start' }}>
                        {settingsSaving ? 'Sparar…' : 'Spara'}
                    </button>
                    {settingsMsg && <p style={{ margin: 0, fontSize: '14px', color: msgColor(settingsMsg) }}>{settingsMsg}</p>}
                </div>
            </div>

            {/* Zigbee OTA Device Configuration */}
            <div style={card}>
                <h3 style={{ marginTop: 0 }}>📡 Zigbee-inställningar i enheten</h3>
                <p style={{ fontSize: '14px', color: '#666', marginBottom: '16px' }}>
                    De här inställningarna skrivs direkt till enheten trådlöst.
                    Enheten måste vara vaken och nåbar för att ändringen ska gå fram.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', maxWidth: '400px' }}>
                    <div>
                        <label style={labelStyle}>Hur ofta enheten hör av sig (kvartssekunder)</label>
                        <input type="number" min="0" step="400" placeholder="t.ex. 14400"
                            value={zigbeeConfigForm.checkinInterval ?? ''}
                            onChange={e => onZigbeeConfigChange(p => ({ ...p, checkinInterval: e.target.value }))}
                            style={inputStyle} />
                        <span style={hintStyle}>
                            Hur ofta enheten vaknar och hämtar meddelanden.
                            14400 = 1 tim | 7200 = 30 min | 3600 = 15 min | 1600 ≈ 7 min.
                        </span>
                    </div>
                    <div>
                        <label style={labelStyle}>Hur länge enheten lyssnar (kvartssekunder)</label>
                        <input type="number" min="0" step="4" placeholder="t.ex. 20"
                            value={zigbeeConfigForm.longPollInterval ?? ''}
                            onChange={e => onZigbeeConfigChange(p => ({ ...p, longPollInterval: e.target.value }))}
                            style={inputStyle} />
                        <span style={hintStyle}>Hur länge enheten lyssnar efter att den hört av sig. 20 = 5 sekunder. Lämna tomt för att behålla nuvarande värde.</span>
                    </div>
                    <button disabled={zigbeeConfigSaving}
                        onClick={() => {
                            const attrs: Record<string, number> = {};
                            if (zigbeeConfigForm.checkinInterval !== '') attrs.checkinInterval = Number(zigbeeConfigForm.checkinInterval);
                            if (zigbeeConfigForm.longPollInterval !== '') attrs.longPollInterval = Number(zigbeeConfigForm.longPollInterval);
                            if (Object.keys(attrs).length === 0) return;
                            onApplyZigbeeConfig(attrs);
                        }}
                        style={{ ...btn(zigbeeConfigSaving ? '#6c757d' : '#007bff'), cursor: zigbeeConfigSaving ? 'not-allowed' : 'pointer', alignSelf: 'flex-start' }}>
                        {zigbeeConfigSaving ? 'Skickar…' : 'Skicka till enheten'}
                    </button>
                    {zigbeeConfigMsg && (
                        <p style={{ margin: 0, fontSize: '14px', color: zigbeeConfigMsg.startsWith('Fel') || zigbeeConfigMsg.startsWith('Delvis') ? '#dc3545' : zigbeeConfigMsg.startsWith('⏳') ? '#856404' : '#28a745' }}>
                            {zigbeeConfigMsg}
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PowerMeterControl;
